import { createContext, useContext, useSyncExternalStore } from 'react';
import type { DocumentId, ProjectId } from '@/shared/types/common.types';
import type { PartTransport } from '@/shared/utils/upload/multipartUpload';
import { createXhrPartTransport } from '@/shared/utils/upload/xhrPartTransport';
import { runDocumentUpload } from '../services/uploadPipeline';
import type { DocumentUpload, UploadStage, UploadTarget } from '../services/uploadPipeline';
import { uploadErrorMessage } from '../utils/uploadErrors';

export interface UploadItem {
  readonly id: string;
  readonly projectId: ProjectId;
  readonly fileName: string;
  readonly title: string;
  /** A new document, or a new revision of an existing one. */
  readonly kind: UploadTarget['kind'];
  readonly stage: UploadStage;
  readonly sentBytes: number;
  readonly totalBytes: number;
  readonly documentId: DocumentId | null;
  /** Why the upload failed, in words for the person; set only when `stage` is `failed`. */
  readonly error: string | null;
  /** Done, but verification had not finished when the queue stopped checking. */
  readonly stillVerifying: boolean;
}

export interface UploadQueue {
  readonly subscribe: (listener: () => void) => () => void;
  readonly getSnapshot: () => readonly UploadItem[];
  /** `onDocumentsChanged` runs when a document appears and when its file is checked. */
  readonly enqueue: (uploads: readonly DocumentUpload[], onDocumentsChanged: () => void) => void;
  /** Stops an upload that has not reached document creation, and drops its row. */
  readonly cancel: (id: string) => void;
  /** Drops a finished row. */
  readonly dismiss: (id: string) => void;
}

export interface UploadQueueOptions {
  readonly concurrency?: number;
  readonly transport?: PartTransport;
  readonly verifyPollMs?: readonly number[];
  readonly run?: typeof runDocumentUpload;
}

/** Quick at first, since most files verify in seconds; about two minutes in all. */
const VERIFY_POLL_MS = [500, 1000, 2000, 3000, 5000, 5000, 10_000, 10_000, 15_000, 15_000, 30_000, 30_000];

export const CANCELLABLE_STAGES: ReadonlySet<UploadStage> = new Set(['queued', 'uploading']);
const FINISHED_STAGES: ReadonlySet<UploadStage> = new Set(['done', 'failed']);

function warnBeforeUnload(event: BeforeUnloadEvent): void {
  event.preventDefault();
}

/**
 * Uploads files two at a time, outside any component, so they keep going while the person
 * moves around the app. Leaving the page while one is running asks for confirmation.
 */
export function createUploadQueue(options: UploadQueueOptions = {}): UploadQueue {
  const concurrency = options.concurrency ?? 2;
  const transport = options.transport ?? createXhrPartTransport();
  const verifyPollMs = options.verifyPollMs ?? VERIFY_POLL_MS;
  const run = options.run ?? runDocumentUpload;

  let items: readonly UploadItem[] = [];
  const listeners = new Set<() => void>();
  const jobs = new Map<
    string,
    {
      readonly upload: DocumentUpload;
      readonly controller: AbortController;
      readonly onDocumentsChanged: () => void;
    }
  >();
  let running = 0;
  let guarding = false;

  const publish = (next: readonly UploadItem[]): void => {
    items = next;
    const busy = items.some((item) => !FINISHED_STAGES.has(item.stage));
    if (busy !== guarding) {
      guarding = busy;
      if (busy) window.addEventListener('beforeunload', warnBeforeUnload);
      else window.removeEventListener('beforeunload', warnBeforeUnload);
    }
    for (const listener of listeners) listener();
  };
  const update = (id: string, patch: Partial<UploadItem>): void =>
    publish(items.map((item) => (item.id === id ? { ...item, ...patch } : item)));

  const start = (id: string): void => {
    const job = jobs.get(id);
    if (job === undefined) return;
    running += 1;
    let created = false;
    void run(
      job.upload,
      {
        onStage: (stage) => update(id, { stage }),
        onProgress: ({ sentBytes, totalBytes }) => update(id, { sentBytes, totalBytes }),
        onSaved: (documentId) => {
          created = true;
          update(id, { documentId });
          job.onDocumentsChanged();
        },
      },
      job.controller.signal,
      { transport, verifyPollMs }
    )
      .then(
        ({ asset }) => update(id, { stage: 'done', stillVerifying: asset.status !== 'Verified' }),
        (error: unknown) => {
          // A cancelled row is already gone.
          if (!job.controller.signal.aborted)
            update(id, { stage: 'failed', error: uploadErrorMessage(error) });
        }
      )
      .finally(() => {
        running -= 1;
        jobs.delete(id);
        if (created) job.onDocumentsChanged();
        pump();
      });
  };

  // Oldest first. `update` replaces `items`, so this walks the list as it was on entry.
  const pump = (): void => {
    for (const item of items) {
      if (running >= concurrency) return;
      if (item.stage === 'queued' && jobs.has(item.id)) {
        update(item.id, { stage: 'uploading' });
        start(item.id);
      }
    }
  };

  return {
    subscribe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    getSnapshot: () => items,
    enqueue: (uploads, onDocumentsChanged) => {
      const added = uploads.map((upload): UploadItem => {
        const id = crypto.randomUUID();
        jobs.set(id, { upload, controller: new AbortController(), onDocumentsChanged });
        return {
          id,
          projectId: upload.projectId,
          fileName: upload.file.name,
          title: upload.title,
          kind: upload.target.kind,
          stage: 'queued',
          sentBytes: 0,
          totalBytes: upload.file.size,
          documentId: null,
          error: null,
          stillVerifying: false,
        };
      });
      publish([...items, ...added]);
      pump();
    },
    cancel: (id) => {
      const item = items.find((candidate) => candidate.id === id);
      if (item === undefined || !CANCELLABLE_STAGES.has(item.stage)) return;
      jobs.get(id)?.controller.abort(new DOMException('The upload was cancelled.', 'AbortError'));
      if (item.stage === 'queued') jobs.delete(id);
      publish(items.filter((candidate) => candidate.id !== id));
    },
    dismiss: (id) => {
      if (items.some((item) => item.id === id && FINISHED_STAGES.has(item.stage))) {
        publish(items.filter((item) => item.id !== id));
      }
    },
  };
}

/** The app's queue. Uploads outlive the dialog and the page that started them. */
export const uploadQueue = createUploadQueue();

const UploadQueueContext = createContext<UploadQueue>(uploadQueue);

/** Swaps in another queue, for tests that need a different transport or faster polling. */
export const UploadQueueProvider = UploadQueueContext.Provider;

export function useUploadQueue(): { readonly queue: UploadQueue; readonly items: readonly UploadItem[] } {
  const queue = useContext(UploadQueueContext);
  const items = useSyncExternalStore(queue.subscribe, queue.getSnapshot);
  return { queue, items };
}
